'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Brain, RefreshCw, Clock, Target, Shield, Activity, AlertTriangle, Download, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import DiagnosisPhaseStream from './DiagnosisPhaseStream';
import RootCauseCard from './RootCauseCard';
import MetricCard from '@/components/ui/MetricCard';
import DiagnosisHistoryChart from './DiagnosisHistoryChart';
import { TabBar, TabTransition } from '@/components/ui/TabTransition';
import SuccessConfirmation from '@/components/ui/SuccessConfirmation';
import { createClient } from '@/lib/supabase/client';

interface DiagnosisFormValues {
  timeRange: string;
  services: string;
  context: string;
  minLevel: string;
}

export type DiagnosisPhase = {
  id: string;
  step: number;
  label: string;
  detail: string;
  status: 'waiting' | 'running' | 'done' | 'error';
  duration?: number;
};

export type RootCauseResult = {
  summary: string;
  detail: string;
  confidence: number;
  category: string;
  affectedServices: string[];
  evidence: { source: string; finding: string; color: string }[];
  suggestedActions: string[];
};

// Skeleton loader component
function SkeletonPhase() {
  return (
    <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
      <div className="skeleton" style={{ width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0 }} />
      <div style={{ flex: 1, paddingTop: '4px' }}>
        <div className="skeleton" style={{ height: '14px', width: '40%', marginBottom: '8px' }} />
        <div className="skeleton" style={{ height: '11px', width: '80%' }} />
      </div>
    </div>
  );
}

// Error boundary component
function DiagnosisErrorBoundary({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div style={{
      backgroundColor: '#0d1117',
      border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: '10px',
      padding: '32px',
      textAlign: 'center',
    }}>
      <AlertTriangle size={32} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
      <div style={{ fontSize: '15px', fontWeight: 600, color: '#e2e8f0', marginBottom: '8px' }}>
        Diagnosis Failed
      </div>
      <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 20px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
        {error}
      </p>
      <button
        onClick={onRetry}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '9px 18px',
          backgroundColor: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '7px',
          cursor: 'pointer',
          color: '#ef4444',
          fontSize: '13px',
          fontWeight: 600,
          transition: 'all 150ms',
        }}
      >
        <RefreshCw size={13} />
        Retry Diagnosis
      </button>
    </div>
  );
}

export default function AIDiagnosisContent() {
  const [diagnosisState, setDiagnosisState] = useState<'idle' | 'running' | 'complete' | 'error'>('idle');
  const [phases, setPhases] = useState<DiagnosisPhase[]>([]);
  const [result, setResult] = useState<RootCauseResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastFormData, setLastFormData] = useState<DiagnosisFormValues | null>(null);
  const [activeTab, setActiveTab] = useState('diagnose');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successConfidence, setSuccessConfidence] = useState(0);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // Track realtime subscription so we can unsubscribe
  const realtimeChannelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  const { register, handleSubmit } = useForm<DiagnosisFormValues>({
    defaultValues: {
      timeRange: '15',
      services: 'api-gateway, payment-service, order-processor',
      context: '',
      minLevel: 'WARN',
    },
  });

  // ── Subscribe to Supabase Realtime for a given job ──────────────────────
  const subscribeToJob = useCallback((jobId: string) => {
    const supabase = createClient();

    // Clean up any existing subscription
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }

    const channel = supabase
      .channel(`diagnosis-job-${jobId}`)
      .on(
        'postgres_changes' as any,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'diagnosis_jobs',
          filter: `id=eq.${jobId}`,
        },
        (payload: any) => {
          const row = payload.new as {
            status: string;
            phases: DiagnosisPhase[];
            result: RootCauseResult | null;
            error_message: string | null;
          };

          if (row.phases && row.phases.length > 0) {
            setPhases(row.phases);
          }

          if (row.status === 'complete' && row.result) {
            setResult(row.result);
            setDiagnosisState('complete');
            setSuccessConfidence(row.result.confidence);
            setShowSuccess(false);
            setTimeout(() => setShowSuccess(true), 50);
            setActiveTab('results');
            toast.success(`AI diagnosis complete — root cause identified with ${row.result.confidence}% confidence`);

            // Unsubscribe once done
            supabase.removeChannel(channel);
            realtimeChannelRef.current = null;
          } else if (row.status === 'failed') {
            const msg = row.error_message || 'Diagnosis pipeline failed. Please retry.';
            setDiagnosisState('error');
            setErrorMessage(msg);
            toast.error('AI diagnosis failed — ' + msg);

            supabase.removeChannel(channel);
            realtimeChannelRef.current = null;
          }
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (realtimeChannelRef.current) {
        const supabase = createClient();
        supabase.removeChannel(realtimeChannelRef.current);
      }
    };
  }, []);

  // ── Enqueue diagnosis job ────────────────────────────────────────────────
  const runDiagnosis = useCallback(async (data: DiagnosisFormValues) => {
    setDiagnosisState('running');
    setResult(null);
    setErrorMessage('');
    setLastFormData(data);
    setPhases([]);
    setActiveTab('diagnose');

    try {
      const res = await fetch('/api/diagnosis/enqueue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const { jobId } = await res.json() as { jobId: string };
      setCurrentJobId(jobId);

      // Subscribe to realtime updates for this job
      subscribeToJob(jobId);

      toast.info('Diagnosis job queued — pipeline starting…');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDiagnosisState('error');
      setErrorMessage(msg);
      toast.error('Failed to start diagnosis — ' + msg);
    }
  }, [subscribeToJob]);

  const handleRetry = useCallback(() => {
    if (lastFormData) {
      runDiagnosis(lastFormData);
    } else {
      setDiagnosisState('idle');
      setPhases([]);
    }
  }, [lastFormData, runDiagnosis]);

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const { generateNexusPDF } = await import('@/lib/utils/generateNexusPDF');
      await generateNexusPDF();
    } catch (err) {
      toast.error('Failed to generate PDF. Please try again.');
      console.error(err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '12px', fontWeight: 600, color: '#94a3b8', display: 'block', marginBottom: '6px',
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', backgroundColor: '#161b22', border: '1px solid #2d3748',
    borderRadius: '6px', color: '#e2e8f0', fontSize: '13px', outline: 'none',
    fontFamily: 'DM Sans, sans-serif', boxSizing: 'border-box',
  };

  const TABS = [
    { id: 'diagnose', label: 'Diagnose' },
    { id: 'results', label: 'Results', badge: diagnosisState === 'complete' ? '✓' : undefined },
    { id: 'history', label: 'History' },
  ];

  const isRunning = diagnosisState === 'running';

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1600px' }}>
      <SuccessConfirmation
        show={showSuccess}
        title="Diagnosis Complete"
        subtitle={`Root cause identified — ${successConfidence}% confidence`}
        onDone={() => setShowSuccess(false)}
        accentColor="#8b5cf6"
      />

      {/* Header */}
      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <Brain size={20} style={{ color: '#8b5cf6' }} />
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#e2e8f0' }}>AI Diagnosis</h1>
            {isRunning && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                fontSize: '11px', color: '#8b5cf6',
                backgroundColor: 'rgba(139,92,246,0.1)',
                border: '1px solid rgba(139,92,246,0.25)',
                padding: '2px 8px', borderRadius: '4px',
              }}>
                <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />
                Background job running
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
            Claude-powered root cause analysis across all connected monitoring sources
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
              backgroundColor: isGeneratingPDF ? 'rgba(139,92,246,0.05)' : 'rgba(139,92,246,0.1)',
              border: '1px solid rgba(139,92,246,0.4)', borderRadius: '6px',
              cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
              color: '#8b5cf6', fontSize: '12px', fontWeight: 600, transition: 'all 150ms',
              opacity: isGeneratingPDF ? 0.7 : 1,
            }}
          >
            <Download size={13} />
            {isGeneratingPDF ? 'Generating…' : 'Download PDF'}
          </button>
          {diagnosisState === 'complete' && (
            <button
              onClick={() => { setDiagnosisState('idle'); setResult(null); setPhases([]); setActiveTab('diagnose'); setCurrentJobId(null); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                backgroundColor: 'transparent', border: '1px solid #2d3748', borderRadius: '6px',
                cursor: 'pointer', color: '#64748b', fontSize: '12px', transition: 'all 150ms',
              }}
            >
              <RefreshCw size={13} />
              New Diagnosis
            </button>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '20px' }}>
        <MetricCard label="CONNECTED SOURCES" value="6" unit="active" trend="flat" trendValue="Splunk · Prometheus · DD · Loki · ES · CW" icon={<Activity size={14} />} accent="blue" />
        <MetricCard label="CORRELATION ACCURACY" value="97.3" unit="%" trend="up" trendValue="+1.2% vs last 7 days" trendPositive={true} icon={<Target size={14} />} accent="green" />
        <MetricCard label="AVG DIAGNOSIS TIME" value="28" unit="sec" trend="down" trendValue="-6s vs last week" trendPositive={true} icon={<Clock size={14} />} accent="cyan" />
        <MetricCard label="INCIDENTS RESOLVED (30D)" value="142" unit="auto" trend="up" trendValue="+23 vs prior period" trendPositive={true} icon={<Shield size={14} />} accent="purple" />
      </div>

      {/* Tab Bar */}
      <div style={{ marginBottom: '16px' }}>
        <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} accentColor="#8b5cf6" />
      </div>

      <TabTransition activeTab={activeTab}>
        {activeTab === 'diagnose' && (
          <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: '20px', alignItems: 'start' }}>
            {/* Left: Config Form */}
            <div style={{ backgroundColor: '#0d1117', border: '1px solid #1e293b', borderRadius: '10px', padding: '20px' }}>
              <h2 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600, color: '#e2e8f0' }}>
                Diagnosis Configuration
              </h2>
              <form onSubmit={handleSubmit(runDiagnosis)}>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Time Range (minutes)</label>
                  <p style={{ fontSize: '11px', color: '#374151', margin: '0 0 6px' }}>How far back to pull logs from all sources</p>
                  <select {...register('timeRange')} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {['5', '15', '30', '60', '120', '360'].map((v) => (
                      <option key={`tr-${v}`} value={v} style={{ backgroundColor: '#161b22' }}>Last {v} minutes</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Target Services</label>
                  <p style={{ fontSize: '11px', color: '#374151', margin: '0 0 6px' }}>Comma-separated service names. Leave blank to analyze all.</p>
                  <input {...register('services')} placeholder="api-gateway, payment-service..." style={inputStyle} />
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Minimum Log Level</label>
                  <select {...register('minLevel')} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {['DEBUG', 'INFO', 'WARN', 'ERROR'].map((v) => (
                      <option key={`ml-${v}`} value={v} style={{ backgroundColor: '#161b22' }}>{v} and above</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Incident Context (optional)</label>
                  <p style={{ fontSize: '11px', color: '#374151', margin: '0 0 6px' }}>Describe symptoms or recent changes to focus the AI analysis</p>
                  <textarea
                    {...register('context')}
                    placeholder="e.g. Payment failures started after the 04:00 UTC deploy..."
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isRunning}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '8px', padding: '11px 20px',
                    backgroundColor: isRunning ? '#1d4ed8' : '#8b5cf6',
                    border: 'none', borderRadius: '7px',
                    cursor: isRunning ? 'not-allowed' : 'pointer',
                    color: '#fff', fontSize: '14px', fontWeight: 600, transition: 'all 150ms',
                    boxShadow: !isRunning ? '0 0 20px rgba(139,92,246,0.3)' : 'none',
                  }}
                >
                  {isRunning ? (
                    <><RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />Analyzing...</>
                  ) : (
                    <><Brain size={15} />Run AI Diagnosis</>
                  )}
                </button>
              </form>

              {/* Job ID badge */}
              {currentJobId && (
                <div style={{
                  marginTop: '14px', padding: '8px 10px',
                  backgroundColor: 'rgba(139,92,246,0.06)',
                  border: '1px solid rgba(139,92,246,0.15)',
                  borderRadius: '6px',
                }}>
                  <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '2px', fontWeight: 600 }}>JOB ID</div>
                  <div className="font-mono" style={{ fontSize: '10px', color: '#8b5cf6', wordBreak: 'break-all' }}>
                    {currentJobId}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Phase Stream */}
            <div>
              {diagnosisState === 'idle' && (
                <div style={{ backgroundColor: '#0d1117', border: '1px dashed #1e293b', borderRadius: '10px', padding: '60px 40px', textAlign: 'center' }}>
                  <Brain size={40} style={{ color: '#374151', margin: '0 auto 16px' }} />
                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>Ready to diagnose</div>
                  <p style={{ fontSize: '13px', color: '#374151', margin: '0 auto', maxWidth: '360px' }}>
                    Configure your time range and target services, then run AI Diagnosis to identify root causes across all connected monitoring sources.
                  </p>
                </div>
              )}
              {diagnosisState === 'error' && (
                <DiagnosisErrorBoundary error={errorMessage} onRetry={handleRetry} />
              )}
              {isRunning && phases.length === 0 && (
                <div style={{ backgroundColor: '#0d1117', border: '1px solid #1e293b', borderRadius: '10px', padding: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <div className="skeleton" style={{ height: '22px', width: '140px', borderRadius: '4px' }} />
                    <div className="skeleton" style={{ height: '14px', width: '100px', borderRadius: '4px' }} />
                  </div>
                  {[1, 2, 3, 4, 5].map(i => <SkeletonPhase key={i} />)}
                </div>
              )}
              {(isRunning || diagnosisState === 'complete') && phases.length > 0 && (
                <DiagnosisPhaseStream phases={phases} />
              )}
            </div>
          </div>
        )}

        {activeTab === 'results' && (
          <div>
            {diagnosisState === 'complete' && result ? (
              <RootCauseCard result={result} />
            ) : (
              <div style={{
                padding: '60px', textAlign: 'center', backgroundColor: '#0d1117',
                border: '1px dashed #1e293b', borderRadius: '10px',
              }}>
                <Brain size={40} style={{ color: '#374151', margin: '0 auto 16px' }} />
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>No results yet</div>
                <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>Run a diagnosis to see root cause analysis results here</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div style={{ backgroundColor: '#0d1117', border: '1px solid #1e293b', borderRadius: '10px', padding: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '16px' }}>DIAGNOSIS HISTORY (14 DAYS)</div>
            <DiagnosisHistoryChart />
          </div>
        )}
      </TabTransition>
    </div>
  );
}